import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ArrowRight, Lock } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { tierStyle } from "../lib/tiers";

/**
 * Encart affiché à la fin de l'aperçu d'un article premium verrouillé.
 * Règle actuelle : tout abonné payant (dès l'offre Essentiel) lit tous les articles premium.
 * - loggedIn = false : visiteur non connecté → lien « Déjà abonné ? Se connecter »
 * - loggedIn = true  : membre gratuit connecté → pas de lien de connexion
 * - subject : « cet article » (par défaut) ou « ce cours »
 * - fade : fondu sur la fin de l'aperçu (désactivé quand il n'y a pas d'aperçu)
 */
export default function ArticlePaywall({ loggedIn = false, subject = "cette analyse", fade = true }) {
  const { data: cfg } = useQuery({
    queryKey: ["config"],
    queryFn: async () => (await api.get("/config")).data,
  });
  const entry = cfg?.tiers?.essentiel;
  const price = entry ? `${entry.price_eur.toString().replace(".", ",")} €/mois` : null;
  const entryName = entry?.name || "Essentiel";

  return (
    <div className="relative mt-2" data-testid="article-paywall">
      {/* Fondu sur la fin de l'aperçu */}
      {fade && (
        <div
          aria-hidden
          className="absolute -top-40 left-0 right-0 h-40 pointer-events-none"
          style={{
            background:
              "linear-gradient(to bottom, rgba(10,10,10,0) 0%, rgba(10,10,10,0.85) 60%, #0A0A0A 100%)",
          }}
        />
      )}

      <div className="relative rounded-xl border border-orange-500/30 bg-gradient-to-br from-orange-500/[0.08] to-transparent p-8 md:p-10 text-center overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-40 grid-noise" />

        <div className="relative inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-500/15 border border-orange-500/40 text-orange-300 text-[11px] uppercase tracking-[0.2em]">
          <Lock className="w-3 h-3" /> Inclus dès l'offre {entryName}
        </div>

        <h3 className="relative mt-5 font-display font-black text-2xl md:text-3xl">
          La suite est réservée aux abonnés
        </h3>
        <p className="relative mt-3 text-zinc-400 max-w-xl mx-auto">
          Débloquez {subject}, toutes les analyses et tous les cours premium, ainsi que le backtest
          et le Wallet DCA. Abonnement sans engagement, résiliable à tout moment.
        </p>

        <div className="relative mt-6 flex flex-col items-center gap-4">
          <Button
            asChild
            size="lg"
            className={`h-11 ${tierStyle("essentiel").cta}`}
            data-testid="paywall-cta"
          >
            <Link to="/upgrade">
              {price ? `Débloquer dès ${price}` : "Débloquer l'analyse"}
              <ArrowRight className="ml-1 w-4 h-4" />
            </Link>
          </Button>
          <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-sm text-zinc-500">
            {price && <span>Offre {entryName}</span>}
            {price && <span>·</span>}
            <Link to="/upgrade" className="hover:text-zinc-200 underline-offset-4 hover:underline">
              Voir toutes les offres
            </Link>
            {!loggedIn && (
              <>
                <span>·</span>
                <Link to="/login" className="hover:text-zinc-200 underline-offset-4 hover:underline">
                  Déjà abonné ? Se connecter
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
